import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Dropdown from '../../../components/Dropdown';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Pagination from '../../../components/TableBox/Pagination';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import Toast from '../../../components/Toast';
import AuditService, { AuditLogEntry } from '../../../services/audit.service';
import InternalUserService from '../../../services/internal-user.service';
import TenantService from '../../../services/tenant.service';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { SortOrder } from '../../../enums';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;

const ENTITY_TYPES = ['tenant', 'user', 'config_key', 'access_review', 'product_branding', 'audit_log'];
const ACTION_TYPES = [
  'tenant.create',
  'tenant.update',
  'tenant.status_transition',
  'tenant.delete',
  'profile.update',
  'profile.avatar_updated',
  'preferences.update',
  'configKey.create',
  'configKey.update',
  'configKey.delete',
  'accessReview.started',
  'accessReview.certify',
  'accessReview.revoke',
  'accessReview.completed',
  'productBranding.update',
  'report.export',
  'audit.export',
];

interface Option {
  id: number;
  label: string;
}

const AuditLogViewer: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'timestamp' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actorId, setActorId] = useState<number | null>(searchParams.get('actorId') ? Number(searchParams.get('actorId')) : null);
  const [entityTypes, setEntityTypes] = useState<string[]>(
    searchParams.get('entityType') ? [searchParams.get('entityType') as string] : []
  );
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [tenantId, setTenantId] = useState<number | null>(
    searchParams.get('tenantId') ? Number(searchParams.get('tenantId')) : null
  );
  const [tenantOptions, setTenantOptions] = useState<Option[]>([]);
  const [tenantSearchInput, setTenantSearchInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [actorOptions, setActorOptions] = useState<Option[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    InternalUserService.list({ includeDeleted: false }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setActorOptions(rows.map((r: any) => ({ id: r.id, label: r.full_name ?? r.email })));
    });
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    TenantService.get(tenantId).then((response: any) => {
      const t2 = response?.data;
      if (t2) setTenantOptions((prev) => [{ id: t2.id, label: t2.tenant_name }, ...prev.filter((o) => o.id !== t2.id)]);
    });
  }, []);

  useEffect(() => {
    if (!tenantSearchInput) return;
    const handle = setTimeout(() => {
      TenantService.list({ search: tenantSearchInput, limit: 20 }).then((response: any) => {
        const rows = response?.data?.items ?? [];
        setTenantOptions(rows.map((r: any) => ({ id: r.id, label: r.tenant_name })));
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [tenantSearchInput]);

  useEffect(() => {
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const filter = useMemo(
    () => ({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      actorId: actorId ?? undefined,
      entityType: entityTypes.length ? entityTypes : undefined,
      actionType: actionTypes.length ? actionTypes : undefined,
      tenantId: tenantId ?? undefined,
      search: debouncedSearch || undefined,
      page,
      limit: pageSize,
    }),
    [startDate, endDate, actorId, entityTypes, actionTypes, tenantId, debouncedSearch, page, pageSize]
  );

  useEffect(() => {
    setLoading(true);
    AuditService.list(filter)
      .then((response: any) => {
        setItems(response?.data?.items ?? []);
        setTotal(response?.data?.pagination?.total ?? 0);
      })
      .finally(() => setLoading(false));
  }, [filter]);

  const handleClearFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setActorId(null);
    setEntityTypes([]);
    setActionTypes([]);
    setTenantId(null);
    setTenantSearchInput('');
    setSearchInput('');
    setDebouncedSearch('');
    setPage(1);
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const response: any = await AuditService.exportCsv(filter);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success(<Toast message={t('auditLog.pageTexts.export.success')} />, { icon: false });
    } finally {
      setExporting(false);
    }
  }, [filter, t]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 className="m-0">{t('auditLog.pageTexts.title')}</h4>
          <p className="text-muted m-0">{t('auditLog.pageTexts.subTitle')}</p>
        </div>
        <Button
          className="btn btn-primary"
          text={t('auditLog.pageTexts.export.button')}
          loading={exporting}
          disabled={total === 0}
          onClick={handleExport}
        />
      </div>

      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="flex-grow-1">
          <Input
            id="audit-search"
            name="search"
            placeholder={t('auditLog.pageTexts.searchPlaceholder')}
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
          />
        </div>
        <IconButton onClick={() => setFiltersOpen((v) => !v)} color={filtersOpen ? 'primary' : 'default'}>
          <FilterListIcon />
        </IconButton>
      </div>

      <Collapse in={filtersOpen}>
        <div className="row g-3 mb-3">
          <div className="col-md-2">
            <Input
              id="startDate"
              name="startDate"
              type="date"
              label={t('auditLog.pageTexts.filters.startDate')}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Input
              id="endDate"
              name="endDate"
              type="date"
              label={t('auditLog.pageTexts.filters.endDate')}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Dropdown
              name="actorId"
              label={t('auditLog.pageTexts.filters.actor')}
              value={actorId ? String(actorId) : ''}
              options={actorOptions.map((o) => ({ id: String(o.id), name: o.label }))}
              noSearch
              onChange={(e) => setActorId(e.target.value ? Number(e.target.value) : null)}
            />
          </div>
          <div className="col-md-2">
            <Dropdown
              name="entityTypes"
              label={t('auditLog.pageTexts.filters.entityType')}
              values={entityTypes}
              options={ENTITY_TYPES.map((type) => ({ id: type, name: type }))}
              multi
              noSearch
              onValuesChange={(vals) => setEntityTypes(vals)}
            />
          </div>
          <div className="col-md-2">
            <Dropdown
              name="actionTypes"
              label={t('auditLog.pageTexts.filters.actionType')}
              values={actionTypes}
              options={ACTION_TYPES.map((type) => ({ id: type, name: type }))}
              multi
              noSearch
              onValuesChange={(vals) => setActionTypes(vals)}
            />
          </div>
          <div className="col-md-2">
            <Dropdown
              name="tenantId"
              label={t('auditLog.pageTexts.filters.tenant')}
              value={tenantId ? String(tenantId) : ''}
              options={tenantOptions.map((o) => ({ id: String(o.id), name: o.label }))}
              onSearch={(query) => setTenantSearchInput(query)}
              onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : null)}
            />
          </div>
          <div className="col-12 d-flex justify-content-end">
            <Button
              className="btn btn-outline-secondary"
              text={t('auditLog.pageTexts.filters.clearAll')}
              onClick={handleClearFilters}
            />
          </div>
        </div>
      </Collapse>

      <div className="obs-table-card card">
        <div className="card-body p-0" style={{ overflowX: 'auto' }}>
          <table className="table obs-table mb-0">
            <thead>
              <tr>
                {[
                  { id: 'created_at', label: t('auditLog.pageTexts.table.columns.timestamp') },
                  { id: 'actor_name_snapshot', label: t('auditLog.pageTexts.table.columns.actor') },
                  { id: 'action_type', label: t('auditLog.pageTexts.table.columns.actionType') },
                  { id: 'entity_type', label: t('auditLog.pageTexts.table.columns.entityType') },
                  { id: 'entity_name', label: t('auditLog.pageTexts.table.columns.entityName') },
                  { id: 'reason', label: t('auditLog.pageTexts.table.columns.reason') },
                  { id: 'ip_address', label: t('auditLog.pageTexts.table.columns.ipAddress') },
                ].map((col) => {
                  const isSorted = orderBy === col.id;
                  return (
                    <th
                      key={col.id}
                      className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (orderBy === col.id) {
                          setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                        } else {
                          setOrderBy(col.id);
                          setOrder(SortOrder.asc);
                        }
                      }}
                    >
                      <span className="d-inline-flex align-items-center gap-1 w-100">
                        <span>{col.label}</span>
                        <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                          {isSorted ? (
                            order === SortOrder.asc ? (
                              <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            ) : (
                              <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                          )}
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRow colSpan={8} />
              ) : sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted text-center py-4">{t('auditLog.pageTexts.table.noDataFoundMessage')}</td>
                </tr>
              ) : (
                sortedItems.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr>
                      <td>{new Date(entry.created_at).toLocaleString()}</td>
                      <td>
                        {entry.actor_name_snapshot ?? entry.actor_full_name ?? '—'}
                        {entry.actor_role_snapshot && <div className="text-muted small">{entry.actor_role_snapshot}</div>}
                      </td>
                      <td>
                        <StatusBadge label={entry.action_type} colour="#3b82f6" />
                      </td>
                      <td>{entry.entity_type ?? '—'}</td>
                      <td>{entry.entity_name_snapshot ?? '—'}</td>
                      <td>{entry.reason ?? '—'}</td>
                      <td>{entry.ip_address ?? '—'}</td>
                      <td className="text-end">
                        {(entry.old_value || entry.new_value) && (
                          <RowActions
                            actions={[
                              {
                                id: 'diff',
                                label: t('auditLog.pageTexts.table.viewDiff') || 'View Diff',
                                icon: <i className="ri-eye-line" />,
                                onClick: () => setExpandedId(expandedId === entry.id ? null : entry.id),
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr>
                        <td colSpan={8} className="bg-light">
                          <div className="row g-3 p-2">
                            <div className="col-md-6">
                              <div className="text-muted small mb-1">{t('auditLog.pageTexts.table.columns.oldValue')}</div>
                              <pre className="mb-0 small">{JSON.stringify(entry.old_value ?? {}, null, 2)}</pre>
                            </div>
                            <div className="col-md-6">
                              <div className="text-muted small mb-1">{t('auditLog.pageTexts.table.columns.newValue')}</div>
                              <pre className="mb-0 small">{JSON.stringify(entry.new_value ?? {}, null, 2)}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )))}
              </tbody>
            </table>
        </div>
        <Pagination
          page={page}
          total={total}
          perPage={pageSize}
          onPageChange={setPage}
          onPerPageChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default AuditLogViewer;
